import { memo, useCallback, useMemo, useState } from "react";
import { getModifierGroupDescription } from "../../helpers";
import {
  CartItemModifierGroupModel,
  CartItemModifierModel,
  StoreModifierGroupModel,
  StoreModifierModel,
} from "../../models";
import ModifierPrice from "./ModifierPrice";
import { Check2 } from "react-bootstrap-icons";

type Props = {
  index: number;
  inflationContext: {
    typeId: number | string;
    hasAdInflation: boolean;
    productPrice: number;
    productBasePrice: number;
  };
  modifierGroup: StoreModifierGroupModel;
  selectedModifiers: CartItemModifierGroupModel[];
  errors?: {
    [g_id: string]: {
      g_name: string;
      message: string;
    };
  };
  handleSelectModifier: (
    modGroup: StoreModifierGroupModel,
    modifier: CartItemModifierModel,
  ) => void;
};

const CommonModifierGroupComponent: React.FC<Props> = ({
  index: groupIndex,
  inflationContext,
  modifierGroup,
  errors = {},
  selectedModifiers,
  handleSelectModifier,
}) => {
  const { g_id: groupId, g_name, modifier: modifiers } = modifierGroup;

  const hasError = Boolean(errors[groupId]);

  return (
    <>
      <div className="addsize_pizza">
        <h4 className="d-flex align-items-center justify-content-start gap-2">
          <label className={`mb-0 ${hasError ? "text-danger" : ""}`}>
            {g_name}
          </label>

          {!selectedModifiers?.length && (
            <span
              className={`modifier-required-text ${hasError ? "text-danger" : ""}`}
            >
             (
              {getModifierGroupDescription(modifierGroup)}
             )
            </span>
          )}
        </h4>
        <ul className="row common-modifier-group">
          {modifiers.map((modifier, modifierIndex) => (
            <ModifierComponent
              key={modifierIndex}
              modifier={modifier}
              modifierGroup={modifierGroup}
              groupIndex={groupIndex}
              handleSelectModifier={handleSelectModifier}
              selectedModifiers={selectedModifiers}
              inflationContext={inflationContext}
            />
          ))}
        </ul>
      </div>
    </>
  );
};

type ModifierComponentProps = {
  groupIndex: number;
  modifier: StoreModifierModel;
  modifierGroup: StoreModifierGroupModel;
  selectedModifiers: CartItemModifierGroupModel[];
  handleSelectModifier: (
    modGroup: StoreModifierGroupModel,
    modifier: CartItemModifierModel,
  ) => void;
  inflationContext: {
    typeId: number | string;
    hasAdInflation: boolean;
    productPrice: number;
    productBasePrice: number;
  };
};

const ModifierComponent: React.FC<ModifierComponentProps> = ({
  modifier,
  modifierGroup,
  groupIndex,
  selectedModifiers,
  handleSelectModifier,
  inflationContext,
}) => {
  const { g_id: groupId, g_max_selection } = modifierGroup;
  const { m_id: modifierId, m_name: modifierName } = modifier;

  const initCartModifier: CartItemModifierModel = selectedModifiers
    .find((g) => g.g_id === groupId)
    ?.modifiers.find((m) => m.m_id === modifierId) ?? {
    m_id: modifierId,
    m_name: modifierName,
    m_price: modifier.m_price,
    m_qty: 1,
    prices: [],
    selected: false,
  };

  const [cartModifier, setCartModifier] =
    useState<CartItemModifierModel>(initCartModifier);

  // Memoize modifier selection logic to avoid recalculation
  const isSelected = useMemo(
    () =>
      selectedModifiers.some(
        (group) =>
          group.g_id === groupId &&
          group.modifiers.some((m) => m.m_id === modifier.m_id),
      ),
    [selectedModifiers, groupId, modifier.m_id],
  );

  // ✅ Total selected modifiers in the group
  const selectedCount = useMemo(
    () =>
      selectedModifiers.find((group) => group.g_id === groupId)?.modifiers
        .length || 0,
    [selectedModifiers, groupId],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedCartModifier = {
      ...cartModifier,
      // prices: [...(prev.prices || []), attribute],
      selected: e.target.checked,
    };

    setCartModifier(updatedCartModifier);
    handleSelectModifier(modifierGroup, updatedCartModifier);
  }, []);

  return (
    <>
      {/* <li key={modifier.m_id} className="col-12 col-sm-6 col-md-6">
        <label>
          <input
            type={g_max_selection == 1 ? "radio" : "checkbox"}
            name={`modifier-group-${groupId}`}
            value={modifier.m_id}
            checked={isSelected}
            onChange={handleChange}
            disabled={
              !isSelected
              && g_max_selection > 0 
              && g_max_selection != 1
              && selectedCount >= g_max_selection
            }
          />{" "}
          <span className="w-100 d-flex justify-content-between">
            <span>{modifier.m_name}</span>
            <span>
              {Number(modifier.m_price) > 0 && (
                <b>
                  <ModifierPrice 
                    inflationContext={inflationContext}
                    groupIndex={groupIndex}
                    modifier={modifier}
                    cartModifier={cartModifier} />
                </b>
              )}
            </span>
          </span>
        </label>
      </li> */}

      <li key={modifier.m_id} className="col-md-6 col-lg-6 col-sm-6">
        <label className={`modifier_option_card ${isSelected ? "active" : ""}`}>
          <input
            type={g_max_selection == 1 ? "radio" : "checkbox"}
            name={`modifier-group-${groupId}`}
            value={modifier.m_id}
            checked={isSelected}
            onChange={handleChange}
            disabled={
              !isSelected &&
              g_max_selection > 0 &&
              g_max_selection != 1 &&
              selectedCount >= g_max_selection
            }
          />

          <div className="modifier_option_inner">
            <div className="modifier_option_left">
              <span className="modifier_check">
                {isSelected && <Check2 size={20} />}
              </span>

              <span className="modifier_name">{modifier.m_name}</span>
            </div>

            {Number(modifier.m_price) > 0 && (
              <div className="modifier_price">
                <ModifierPrice
                  inflationContext={inflationContext}
                  groupIndex={groupIndex}
                  modifier={modifier}
                  cartModifier={cartModifier}
                />
              </div>
            )}
          </div>
        </label>
      </li>
    </>
  );
};

export const CommonModifierGroup = memo(CommonModifierGroupComponent);
